import {
  ListKPIsResponse,
  ListKPIsResponse_KPI,
} from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/aqora_service_pb';
import { MetricType } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/metrics_pb';
import {
  KpiModel,
  KPI_V2_DRAINED_TOTAL,
  SyntheticMetricType,
} from '~/pages/Dashboard/models/KpiModel';
import { getRoundedKpiValue } from '~/pages/Dashboard/utils/kpi-calculations';

export type SystemAutomationMode = 'outsideGrowing' | 'growing';

export type AutomationModeKpiGroup = {
  automationMode: SystemAutomationMode;
  incomingWaterKpis: KpiModel[];
  outgoingWaterKpis: KpiModel[];
};

const AUTOMATION_MODES: SystemAutomationMode[] = ['outsideGrowing', 'growing'];

const INCOMING_WATER_KPI_TYPES = [
  MetricType.KPI_V2_RAIN_TOTAL_L,
  MetricType.KPI_V2_SOIL_LOSS_L,
  MetricType.KPI_V2_TAP_USAGE_L,
] as const;

const OUTGOING_WATER_KPI_TYPES = [
  MetricType.KPI_V2_PLANT_USAGE_L,
  KPI_V2_DRAINED_TOTAL,
  MetricType.KPI_V2_OVERFLOW_L,
] as const;

type KpiType = MetricType | SyntheticMetricType;

function isKpiTypeAvailable(type: KpiType, availableMetricTypes: MetricType[]) {
  if (type === KPI_V2_DRAINED_TOTAL) {
    return (
      availableMetricTypes.includes(MetricType.KPI_V2_DRAINED_DELAYED_L) ||
      availableMetricTypes.includes(MetricType.KPI_V2_DRAINED_DIRECT_L)
    );
  }

  return availableMetricTypes.includes(type);
}

function filterAvailableKpiTypes(
  types: readonly KpiType[],
  availableMetricTypes: MetricType[],
) {
  return types.filter((type) => isKpiTypeAvailable(type, availableMetricTypes));
}

export class KpiListModel {
  readonly automationModeKpiGroups: AutomationModeKpiGroup[];

  constructor(response: ListKPIsResponse, availableMetricTypes: MetricType[]) {
    this.automationModeKpiGroups = AUTOMATION_MODES.flatMap(
      (automationMode) => {
        const automationModeGroup = this.createAutomationModeKpiGroup(
          automationMode,
          response[automationMode],
          availableMetricTypes,
        );

        return automationModeGroup ? [automationModeGroup] : [];
      },
    );
  }

  private createAutomationModeKpiGroup(
    automationMode: SystemAutomationMode,
    kpis: ListKPIsResponse_KPI[],
    availableMetricTypes: MetricType[],
  ): AutomationModeKpiGroup | null {
    const kpiByType = this.createKpiByTypeMap(kpis, availableMetricTypes);
    const incomingWaterKpis = this.getKpisFromResponse(
      kpiByType,
      filterAvailableKpiTypes(INCOMING_WATER_KPI_TYPES, availableMetricTypes),
    );
    const outgoingWaterKpis = this.getKpisFromResponse(
      kpiByType,
      filterAvailableKpiTypes(OUTGOING_WATER_KPI_TYPES, availableMetricTypes),
    );
    const hasData =
      incomingWaterKpis.length > 0 || outgoingWaterKpis.length > 0;

    if (!hasData) {
      return null;
    }

    return {
      automationMode,
      incomingWaterKpis,
      outgoingWaterKpis,
    };
  }

  private createKpiByTypeMap(
    kpis: ListKPIsResponse_KPI[],
    availableMetricTypes: MetricType[],
  ) {
    const kpiByType = new Map<KpiType, KpiModel>();

    for (const kpi of kpis) {
      if (!isKpiTypeAvailable(kpi.type, availableMetricTypes)) {
        continue;
      }

      kpiByType.set(kpi.type, this.createKpiModelFromListKpis(kpi));
    }

    if (isKpiTypeAvailable(KPI_V2_DRAINED_TOTAL, availableMetricTypes)) {
      this.buildDrainedTotalKpiInMap(kpiByType);
    }

    return kpiByType;
  }

  private buildDrainedTotalKpiInMap(kpiMap: Map<KpiType, KpiModel>) {
    const delayed = kpiMap.get(MetricType.KPI_V2_DRAINED_DELAYED_L);
    const direct = kpiMap.get(MetricType.KPI_V2_DRAINED_DIRECT_L);

    if (
      (!delayed || delayed.value === undefined) &&
      (!direct || direct.value === undefined)
    ) {
      return;
    }

    const totalValue = (delayed?.value ?? 0) + (direct?.value ?? 0);
    const dates = [delayed?.date, direct?.date].filter(
      (date): date is Date => date !== undefined,
    );
    const latestDate =
      dates.length > 0
        ? new Date(Math.max(...dates.map((date) => date.getTime())))
        : undefined;

    kpiMap.set(
      KPI_V2_DRAINED_TOTAL,
      new KpiModel(KPI_V2_DRAINED_TOTAL, totalValue, latestDate),
    );
  }

  private createKpiModelFromListKpis(kpi: ListKPIsResponse_KPI) {
    const roundedUsage = getRoundedKpiValue(kpi.value);
    const value = kpi.valid ? (roundedUsage > 0 ? roundedUsage : 0) : undefined;

    return new KpiModel(kpi.type, value);
  }

  private getKpisFromResponse(
    kpiMap: Map<KpiType, KpiModel>,
    types: readonly KpiType[],
  ) {
    return types.flatMap((type) => {
      const kpi = kpiMap.get(type);

      return kpi && kpi.value !== undefined ? [kpi] : [];
    });
  }
}
